import { Button, Stack } from '@mui/material';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { FormProvider } from 'react-hook-form';

import { isNotEmptyArray, isNotNullOrUndefined } from '@vy/utils';

import { useCustomer } from '../../../hooks/requests/use-customer';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { CreateCustomerAdvanced } from '../CreateCustomerPage/create-customer-page-components/CreateCustomerAdvanced/CreateCustomerAdvanced';
import { UpdateCustomerCompanyDetails } from './update-customer-settings-components/UpdateCustomerChildren/UpdateCustomerCompanyDetails';
import { UpdateCustomerConfiguration } from './update-customer-settings-components/UpdateCustomerConfiguration';
import { CustomerSettingsCustomFields } from './update-customer-settings-components/CustomerSettingsCustomFields';
import { useUpdateCustomerForm } from './update-customer-settings-hooks/use-update-customer-forms';
import { UpdateCustomerSettingsSkeleton } from './update-customer-settings-components/update-customer-settings-skeleton';
import { buildCustomFieldRows } from '../../../components/custom-fields/build-custom-field-rows';

const UpdateCustomerSettings: React.FC = () => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');
  const { data: customerData } = useCustomer(customerId);

  const customFieldRows = useMemo(
    () =>
      buildCustomFieldRows(customerData?.customFieldsWithDefinitions, customerData?.customFieldDefinitions),
    [customerData?.customFieldsWithDefinitions, customerData?.customFieldDefinitions],
  );

  const { methods, onSubmit } = useUpdateCustomerForm(
    customerId,
    customerData?.customer,
    customerData?.children,
    customFieldRows,
  );

  return (
    <>
      <Helmet>
        <title>Customer Settings</title>
      </Helmet>
      <FormProvider {...methods}>
        <form
          onSubmit={async (e) => {
            e.preventDefault();

            await onSubmit();
          }}
        >
          <div className="relative flex h-fit min-h-fit flex-col gap-5">
            <UpdateCustomerCompanyDetails />
            {isNotNullOrUndefined(customerData?.customer) && (
              <UpdateCustomerConfiguration customer={customerData.customer} />
            )}
            <CreateCustomerAdvanced
              mode="update"
              customer={customerData?.customer}
              customerChildren={customerData?.children}
              childrenRelations={customerData?.childrenRelations}
            />
            {isNotEmptyArray(customFieldRows) && (
              <CustomerSettingsCustomFields customFields={customFieldRows} />
            )}
          </div>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "flex-end",
              position: "sticky",
              bottom: -14,
              backgroundColor: '#FFFFFF',
              paddingLeft: 3,
              paddingRight: 3,
              paddingTop: 2,
              paddingBottom: 2,
              marginLeft: -3,
              marginRight: -3,
              zIndex: 1
            }}>
            <Button
              onClick={() => methods.reset()}
              size="large"
              type="button"
              variant="outlined"
              color="primary"
            >
              Clear
            </Button>
            <Button
              size="large"
              type="submit"
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};

const UpdateCustomerSettingsWithSuspenseBoundary = withSuspenseBoundary(UpdateCustomerSettings, {
  suspenseProps: {
    fallback: <UpdateCustomerSettingsSkeleton />,
  },
});

export { UpdateCustomerSettingsWithSuspenseBoundary as UpdateCustomerSettings };
